import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '../lib/api';
import type { SourceDto, SourceType } from '@shared/types';

const KEY = ['admin', 'sources'] as const;

export interface SourceInput {
  name: string;
  type: SourceType;
  url: string;
  configJson?: string | null;
  trustScore: number;
  isEnabled: boolean;
}

export function useAdminSources() {
  const qc = useQueryClient();

  const list = useQuery({
    queryKey: KEY,
    queryFn: async () => (await api.get<{ data: SourceDto[] }>('/admin/sources')).data,
  });

  const create = useMutation({
    mutationFn: (input: SourceInput) =>
      api.post<{ data: { id: string } }>('/admin/sources', input),
    onSuccess: () => qc.invalidateQueries({ queryKey: KEY }),
  });

  const update = useMutation({
    mutationFn: ({ id, ...input }: SourceInput & { id: string }) =>
      api.patch<{ data: { id: string } }>(`/admin/sources/${id}`, input),
    onSuccess: () => qc.invalidateQueries({ queryKey: KEY }),
  });

  const remove = useMutation({
    mutationFn: (id: string) => api.delete<{ data: { id: string } }>(`/admin/sources/${id}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: KEY }),
  });

  return { list, create, update, remove };
}
